import React, { useState } from 'react';
import '../../css/landing/aboutUs.scss';
import { Typography } from '@mui/material';

// Sourced from https://chensivan.github.io/ (the lab's own site) for photos
// and current-vs-alumni status, but titles follow the site's existing
// convention (Dr. Chen as Project Manager, everyone else as Developer)
// rather than the lab site's own titles. Kevin Pu is on the lab site but is
// not one of Dr. Chen's direct PhD students, so he's excluded here. Shiva is
// listed last per explicit request, independent of the source page's order.
const currentTeam = [
  { name: 'Yan Chen', role: 'Project Manager', imageUrl: 'https://chensivan.github.io/files/headshot1.jpg', website: 'https://chensivan.github.io/' },
  { name: 'Xiaohang Tang', role: 'Developer', imageUrl: 'https://chensivan.github.io/img/profile_image/xiaohang_tang.jpeg', website: 'https://xiaohang-tang.github.io/' },
  { name: 'David Barron', role: 'Developer', imageUrl: 'https://chensivan.github.io/img/profile_image/david_barron.png', website: 'https://www.davidbarron.dev/' },
  { name: 'Panayu Keelawat', role: 'Developer', imageUrl: 'https://chensivan.github.io/img/profile_image/panayu.jpg', website: 'https://gpanayu.github.io/' },
  { name: 'Shiva Pochampally', role: 'Developer', imageUrl: 'https://chensivan.github.io/img/profile_image/shiva_pochampally.png', website: 'https://sathwik.com/' },
];

// Everyone previously listed here who is no longer on the lab's current
// roster. Alumni are name-only, no photos/roles/links.
const alumni = [
  'Xi Chen',
  'Tejas Navada',
  'Susan He',
  'Yusi Yao',
  'Rexime Abulikemu',
  'Ninglan Lei',
  'Bogdan Perlroth',
  'Sam Wong',
  'Saksham Kumar',
  'Raman Khatri',
];

// One shared card shape for every entry: title, venue+year, thumbnail,
// one-sentence project description, and paper/demo links (when available).
// Newest first. The three older entries (VizGroup/CFlow/VizProg) previously
// used a different format (authors baked into the title, multiple labeled
// demo links, a separate "thoughts" field) - reshaped here to match the
// three featured entries above them, one-sentence descriptions rewritten
// from each paper's actual abstract rather than the old per-link labels.
const findings = [
  {
    title: 'TutorTrace: A Dataset and Taxonomy for Classifying Learner Behavioral States during AI-Assisted Programming Education',
    venue: 'UIST',
    year: '2026',
    imageUrl: 'https://chensivan.github.io/img/tutortrace.png',
    keyFinding: 'TutorTrace presents a large-scale dataset and behavioral taxonomy that makes learners’ real-time struggle and progress visible and computable from raw IDE telemetry, giving AI programming tutors the behavioral context human tutors rely on to know when and how to help.',
    paperLink: 'https://chensivan.github.io/papers/uist26_tutortrace.pdf',
    demoLink: 'https://vizpi.org/dataset',
  },
  {
    title: 'Dynamite: Real-Time Debriefing Slide Authoring through AI-Enhanced Multimodal Interaction',
    venue: 'VL/HCC',
    year: '2025',
    imageUrl: 'https://arxiv.org/html/2507.20137v1/myedit2teaser.png',
    keyFinding: 'Dynamite presents an AI-assisted authoring system that lets instructors update debriefing slides live during classroom discussions with voice and sketch input, making it easier to accurately capture and present students’ evolving ideas in real time.',
    paperLink: 'https://arxiv.org/abs/2507.20137',
  },
  {
    title: 'The Impact of Group Discussion and Formation on Student Performance: An Experience Report in a Large CS1 Course',
    venue: 'SIGCSE',
    year: '2025',
    imageUrl: 'https://chensivan.github.io/img/vizpi_sigcse25.jpg',
    keyFinding: 'This experience report examines how group formation and discussion dynamics shape student performance in a large CS1 course, offering instructors evidence-based guidance on grouping struggling students with expert peers during collaborative learning activities.',
    paperLink: 'https://arxiv.org/abs/2408.14610',
  },
  {
    title: 'VizGroup: An AI-Assisted Event-Driven System for Real-Time Collaborative Programming Learning Analytics',
    venue: 'UIST',
    year: '2024',
    imageUrl: 'https://chensivan.github.io/img/vizgroup.png',
    keyFinding: 'VizGroup presents an AI-assisted, event-driven system that lets instructors oversee students’ real-time collaborative learning behaviors in large programming courses, using LLMs to recommend event specifications and alert instructors to key correlation patterns as they emerge.',
    paperLink: 'https://chensivan.github.io/papers/vizgroup_uist_2024.pdf',
    demoLink: 'https://www.dropbox.com/scl/fi/gmfexd5qd0il12zuxg7m6/uist-preview.mp4?rlkey=34j2i3odgau6visikzae9ttq6&dl=0',
  },
  {
    title: "CFlow: Supporting Semantic Flow Analysis of Students' Code in Programming Problems at Scale",
    venue: 'L@S',
    year: '2024',
    imageUrl: 'https://chensivan.github.io/img/cflow.png',
    keyFinding: 'CFlow presents a code-flow visualization system that clusters thousands of student code submissions by semantic similarity into a single navigable representation, helping instructors spot class-wide problem-solving patterns and mistakes at scale.',
    paperLink: 'https://arxiv.org/abs/2404.10089',
    demoLink: 'https://www.dropbox.com/s/mlw6y2r5ceyjesz/CFlow-v1.m4v?dl=0',
  },
  {
    title: "VizProg: Identifying Misunderstandings by Visualizing Students' Coding Progress",
    venue: 'CHI',
    year: '2023',
    imageUrl: 'https://chensivan.github.io/img/vizprog.png',
    keyFinding: "VizProg presents an innovative, real-time visualization system that meaningfully displays class-wide students' coding progress, making it simple for programming instructors to pinpoint issues and mistakes.",
    paperLink: 'https://chensivan.github.io/papers/chi2023_vizprog.pdf',
  },
];

const AboutUsPanel = () => {

    return (
        <>
        <div className="about-us">
            <div className="header-container">
                <Typography variant='h2' className="header-text">
                    Our team
                </Typography>
                <Typography variant='body1' className="intro-text">
                VizPI is a platform developed by a dedicated group of researchers, instructors, student designers, and student engineers from institutions like Virginia Tech, University of Virginia, University of Washington, and more, all united by a passion for transforming education. Our platform is designed to help instructors conduct dynamic in-class coding exercises that engage students in meaningful peer interactions while providing deep insights into their cognitive processes and understanding of programming concepts.                </Typography>
                <hr className="division-line" />
                <br/>
            </div>

            <Typography variant='h4' className="team-section-heading">
                Current Team
            </Typography>
            <div className="profile-container">
                {currentTeam.map((profile, index) => (
                    <div className="profile" key={index}>
                        <div className="profile-description">
                            {profile.role}
                        </div>
                        <div className="profile-pic" style={{ backgroundImage: `url(${profile.imageUrl})` }}></div>
                        <a href={profile.website} target="_blank" rel="noopener noreferrer" className="profile-name">
                            {profile.name}
                        </a>
                    </div>
                ))}
            </div>

            <Typography variant='h4' className="team-section-heading">
                Alumni
            </Typography>
            <ul className="alumni-list">
                {alumni.map((name) => (
                    <li key={name}>{name}</li>
                ))}
            </ul>

            <br/>
            <hr className="division-line"/>
            
            </div>


            <div className="intro-container">
            
                <div className="header-container">
                    <Typography variant='h3' className="exersice-header-text">
                        Previous Findings
                    </Typography>
                    <Typography variant='body1' className="intro-text">
                        Our previous findings highlight the positive impact of AI-assisted monitoring on collaborative programming learning. Instructors reported improved student engagement and more effective collaboration, with AI-generated insights helping to identify and address learning gaps promptly. These results demonstrate the potential of VizGroup to transform educational experiences by fostering deeper understanding and teamwork.
                    </Typography>
                    <br/>
                    <hr className="division-line" />
                </div>
                </div>
                <div className="about-us">
                <div className="articles-container">
                    {findings.map((finding, index) => (
                        <div className="article finding-card" key={index}>
                            <div className="article-image" style={{ backgroundImage: `url(${finding.imageUrl})` }} />
                            <div className="article-content">
                                <span className="finding-venue">{finding.venue} {finding.year}</span>
                                <Typography variant='h6' className="article-title">
                                    {finding.title}
                                </Typography>
                                <Typography variant="body2" className="article-description finding-key">
                                    {finding.keyFinding}
                                </Typography>
                                <div className="article-meta">
                                    <div className="article-links">
                                        {finding.paperLink && (
                                            <a href={finding.paperLink} target="_blank" rel="noopener noreferrer" className="article-link">
                                                paper
                                            </a>
                                        )}
                                        {finding.demoLink && (
                                            <>
                                                {' | '}
                                                <a href={finding.demoLink} target="_blank" rel="noopener noreferrer" className="article-link">
                                                    demo
                                                </a>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            </>

    );
};

export default AboutUsPanel;